import classNames from 'classnames';
import { getLocale } from '@libs/constants';
import { EXTERNAL_LINKS } from '@libs/redirect.helper';
import { useTranslation } from 'next-i18next';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { NextLink } from '@common/NextLink/NextLink';

import { useRouter } from 'next/router';
import type { UrlObject } from 'url';

export interface BreadcrumbItem {
  title: string;
  url: string | UrlObject | null;
  key?: string;
  parentKeys?: string[];
  isExternal?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  addHome?: boolean;
}

export const Breadcrumb = ({ items, addHome }: BreadcrumbProps) => {
  const { t } = useTranslation('layout/breadcrumb');
  const router = useRouter();
  const locale = getLocale(router);
  const breadcrumbItems: BreadcrumbItem[] = addHome
    ? [{ url: EXTERNAL_LINKS.SHOP_HOMEPAGE(locale), title: t('BREADCRUMB.HOME'), isExternal: true }, ...items]
    : items;

  return (
    <div className="mb-10 px-2 text-xxs igus-text-styles" data-testid="breadcrumb">
      {breadcrumbItems.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index}>
          <div className={classNames(['inline-block', 'mr-4', index > 0 ? 'ml-6' : null])}>
            <Icon id={IconID.ARROW_RIGHT} size={3} color={IconColor.ORANGE} />
          </div>
          {(() => {
            if (!item.url) {
              // breadcrumb item without linking anywhere
              return <span data-testid={`breadcrumb-item-${index + 1}`}>{item.title}</span>;
            }
            return item.isExternal ? (
              <a data-testid={`breadcrumb-external-item-${index + 1}`} href={item.url as string} className="text-xxs">
                {item.title}
              </a>
            ) : (
              <NextLink href={item.url} data-testid={`breadcrumb-item-${index + 1}`} className="text-xxs">
                {item.title}
              </NextLink>
            );
          })()}
        </span>
      ))}
    </div>
  );
};
